import React, { useEffect, useState } from "react";
import { Bell, Clock, Gift, Wallet, User, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";

import NotificationToggle from "../../components/NotificationToggle";
import { fetchNotificationSettings, updateNotificationSettings } from "../../service/userService";
import { getPushSubscription } from "../../utils/pushNotifications";
import "../../assets/css/NotificationSettings.css";

const DEFAULT_SETTINGS = {
  all_notifications: false,
  new_survey_available: false,
  survey_reminders: false,
  reward_received: false,
  wallet_updates: false,
  referral_notifications: false,
};

const NOTIFICATION_KEYS = Object.keys(DEFAULT_SETTINGS);
const INDIVIDUAL_NOTIFICATION_KEYS = NOTIFICATION_KEYS.filter((key) => key !== "all_notifications");

const normalizeSettings = (data = {}) =>
  NOTIFICATION_KEYS.reduce((settings, key) => {
    settings[key] = data[key] === true || Number(data[key]) === 1;
    return settings;
  }, {});

const SECTIONS = [
  {
    title: "Survey Notifications",
    rows: [
      {
        key: "new_survey_available",
        icon: Bell,
        title: "New Survey Available",
        subtitle: "Get notified when a survey matching your profile is available.",
      },
      {
        key: "survey_reminders",
        icon: Clock,
        title: "Survey Reminders",
        subtitle: "Receive reminders for surveys before they expire.",
      },
    ],
  },
  {
    title: "Rewards & Wallet",
    rows: [
      {
        key: "reward_received",
        icon: Gift,
        title: "Reward Received",
        subtitle: "Get notified whenever you earn survey rewards.",
      },
      {
        key: "wallet_updates",
        icon: Wallet,
        title: "Wallet Updates",
        subtitle: "Receive notifications for wallet credits, debits, and balance updates.",
      },
    ],
  },
  {
    title: "Referral",
    rows: [
      {
        key: "referral_notifications",
        icon: User,
        title: "Referral Notifications",
        subtitle: "Get updates when your referrals join or earn rewards.",
      },
    ],
  },
];

const NotificationRow = ({ icon: Icon, title, subtitle, checked, onChange, disabled }) => (
  <div className="ns-row d-flex align-items-center justify-content-between">
    <div className="d-flex align-items-center gap-3">
      <div className="ns-icon-chip">
        <Icon size={18} />
      </div>
      <div>
        <div className="ns-row-title">{title}</div>
        <div className="ns-row-subtitle">{subtitle}</div>
      </div>
    </div>
    <NotificationToggle label="" variant="red" fullWidth={false} checked={checked} onChange={onChange} disabled={disabled} />
  </div>
);

const NotificationSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [savingType, setSavingType] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchNotificationSettings()
      .then((res) => {
        if (cancelled || !res?.data) return;
        setSettings(normalizeSettings(res.data));
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || "Unable to load notification settings.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const saveSetting = async (type, enabled, optimisticSettings) => {
    const previousSettings = settings;
    setSavingType(type);

    try {
      const pushSubscription = enabled ? await getPushSubscription() : undefined;
      setSettings(optimisticSettings);

      const response = await updateNotificationSettings(type, enabled, pushSubscription);
      if (response?.success === false) {
        throw new Error(response.message || "Unable to update notification settings.");
      }
      if (response?.data) setSettings(normalizeSettings(response.data));
    } catch (error) {
      setSettings(previousSettings);
      toast.error(error?.response?.data?.message || error?.message || "Unable to update notification settings.");
    } finally {
      setSavingType(null);
    }
  };

  const handleToggle = (key) => async (enabled) => {
    const nextSettings = { ...settings, [key]: enabled };
    nextSettings.all_notifications = INDIVIDUAL_NOTIFICATION_KEYS.every((settingKey) => nextSettings[settingKey]);
    await saveSetting(key, enabled, nextSettings);
  };

  const allEnabled = settings.all_notifications;

  const handleEnableAll = async (enabled) => {
    const next = NOTIFICATION_KEYS.reduce((acc, key) => {
      acc[key] = enabled;
      return acc;
    }, {});
    await saveSetting("all", enabled, next);
  };

  const controlsDisabled = isLoading || savingType !== null;

  return (
    <div className="dashboard-search-results notification-settings">
      <div className="row g-3">
        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <h1 className="sr-page-title">Notification Settings</h1>
            <p className="sr-page-subtitle">Manage How You Want To Stay Updated.</p>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="ns-row-title">Enable All Notifications</div>
                <div className="ns-row-subtitle">Turn on all notification types with one click.</div>
              </div>
              <NotificationToggle label="" variant="red" fullWidth={false} checked={allEnabled} onChange={handleEnableAll} disabled={controlsDisabled} />
            </div>
          </div>
        </div>

        {SECTIONS.map((section) => (
          <div className="col-12" key={section.title}>
            <div className="sr-shell-card p-3 p-md-4">
              <div className="ns-section-title">{section.title}</div>
              {section.rows.map((row) => (
                <NotificationRow
                  key={row.key}
                  icon={row.icon}
                  title={row.title}
                  subtitle={row.subtitle}
                  checked={Boolean(settings[row.key])}
                  onChange={handleToggle(row.key)}
                  disabled={controlsDisabled}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="col-12">
          <div className="ns-privacy-banner d-flex align-items-start gap-2">
            <ShieldCheck size={18} />
            <div>
              <div className="ns-privacy-title">Your preferences are private and secure.</div>
              <div>We'll only use your settings to send relevant notifications.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
